/*
* User add view for authenticated users
*/

import * as React from 'react';
import * as _ from 'lodash';
import ReactImageFallback from 'react-image-fallback';
import { Row, Well, Button, ButtonToolbar, ButtonGroup, Modal } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { connectRequest } from 'redux-query';
import { push } from 'react-router-redux';
import { withRouter } from 'react-router-dom';

import * as AppComp from '../common/app-component-base';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import * as LK from '../config/apilookupkeys';
import * as MutateActions from '../actions/mutate';
import * as ReduxQueries from '../queries';
import * as UserComponents from '../common/user-components';
import ApiEndpoints from '../config/apiendpoints';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { NavigationLinks } from '../common/navigation';

export const DefaultNewUserRoleIds: Array<string> = ['5b5c9e45171a7d764a7241c3']; // Employee role

export interface IUserAddProps extends AppComp.IAppComponentProps {
    lookups?: any;
    newUser?: any;
    navigateTo?: any;
}

export interface IUserAddState extends AppComp.IAppComponentState {
    formChanges?: any;
    showModalResetPassword?: boolean;
    showModalNewUser?: boolean;
}

class AddUser extends AppComp.AppComponent<IUserAddProps, IUserAddState> {
    public originalFormDataCache = {};

    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: IUserAddProps) {
        super(props, 'app.user-add.');

        this.state = {
            formChanges: {},
            showModalResetPassword: false,
            showModalNewUser: true,
        };
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOnly();

        if (enforce) {
            this.props.navigateTo(NavigationLinks.userDashboard());
        }
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // TRANSFORMS

    // HANDLERS        
    public handleShowModalNewUser(e: any, formData: any) {
        let formChanges = _.cloneDeep(this.state.formChanges);
        const newUser = this.getDefaultNewUserObject(_.get(formData, 'userRoleOptions', []));
        // console.log(`handleShowModalNewUser`, { newUser });

        _.set(formChanges, 'newUser', newUser);

        this.setState({ formChanges: formChanges, showModalNewUser: true });
    }

    public handleCloseModalNewUser() {
        this.setState({ showModalNewUser: false });
    }

    public handleNewUserButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const { lookups, ...rest } = this.props;
        const formData = this.getFormData(lookups);

        const newUserFormChanges = _.get(this.state, 'formChanges.newUser');
        const defaultNewUser = this.getDefaultNewUserObject(_.get(formData, 'userRoleOptions', []));

        let newUserMerged = _.merge({}, defaultNewUser, newUserFormChanges);
        let payload = _.cloneDeep(newUserMerged);
        // console.log(`handleNewUserButton`, { newUserFormChanges, defaultNewUser, newUserMerged, payload });
        // let payload = _.cloneDeep(_.get(this.state, 'formChanges.newUser'));

        if (Formatters.getStr(formData, 'tempPassword')) {
            _.set(payload, 'tempPassword', Formatters.getStr(formData, 'tempPassword'));
        }

        const changeData = {
            newUser: payload
        };

        this.props.newUser(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                // this.setState({ showModalNewUser: false });
                this.setState({ formChanges: {}, showModalNewUser: false });

                // FORCE RELOAD
                const newUserObjId = _.get(response, 'body._id');

                this.props.navigateTo(NavigationLinks.userEditObjId(newUserObjId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    public handleFormNewUserRoleChange(e: any, x: any) {
        const newVal = _.get(e, 'target.value');

        const { lookups, ...rest } = this.props;

        const formData = this.getFormData(lookups);

        let cloneFormChanges = _.cloneDeep(this.state.formChanges);
        _.set(cloneFormChanges, 'newUser.userRolesAssigned', newVal);

        this.setState({ formChanges: cloneFormChanges });
    }

    public handleFormNewUserRegionChange(e: any, x: any) {
        const newVal = _.get(e, 'target.value');

        const { lookups, ...rest } = this.props;

        const formData = this.getFormData(lookups);

        let cloneFormChanges = _.cloneDeep(this.state.formChanges);
        _.set(cloneFormChanges, 'newUser.userRegionsAssigned', newVal);

        this.setState({ formChanges: cloneFormChanges });
    }

    public handleFormNewUserAdditionalGroupChange(e: any, x: any) {
        const newVal = _.get(e, 'target.value');

        const { lookups, ...rest } = this.props;

        const formData = this.getFormData(lookups);

        let cloneFormChanges = _.cloneDeep(this.state.formChanges);
        _.set(cloneFormChanges, 'newUser.userAdditionalGroupsAssigned', newVal);

        // console.log(`handleFormNewUserAdditionalGroupChange`, { cloneFormChanges, e, x });

        this.setState({ formChanges: cloneFormChanges });
    }

    // HELPERS
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                case 'email':
                case 'password':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            // if (String(newValue) !== String(origVal)) {
            // New value
            _.set(formChangesCopy, fieldName, newValue);
            _.set(formChangesResult, fieldName, newValue);
            applyChange = true;
            // } else {
            //     // Same as orig val
            //     if (_.has(formChangesCopy, fieldName)) {
            //         _.unset(formChangesCopy, fieldName);
            //         _.set(formChangesResult, fieldName, newValue);
            //         applyChange = true;
            //     }
            // }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public getFormData(lookups: any) {
        const { users, roles, tempPasswords, regions, groups } = lookups;

        let userRoleOptions: any = _.stubArray();
        if (_.size(roles)) {
            userRoleOptions = _.orderBy(roles, ['name'], ['asc']).map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.getStr(c, 'name') }));
        }

        let userRegionOptions: any = _.stubArray();
        if (_.size(regions)) {
            userRegionOptions = _.orderBy(regions, ['name'], ['asc']).map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.getStr(c, 'name') }));
        }

        let userAdditionalGroupOptions: any = _.stubArray();
        if (_.size(groups)) {
            userAdditionalGroupOptions = _.orderBy(groups, ['description'], ['asc']).map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.getStr(c, 'description') }));
        }

        // console.log(`getFormData`, { groups, lookups, userAdditionalGroupOptions });

        const newUserFormChanges = _.get(this.state.formChanges, 'newUser');
        const defaultNewUser = this.getDefaultNewUserObject(userRoleOptions);

        let newUserMerged = _.merge({}, defaultNewUser, newUserFormChanges);

        let result = {
            newUser: newUserMerged,

            userRoleOptions: (_.size(userRoleOptions)) ? userRoleOptions : [],
            userRegionOptions: (_.size(userRegionOptions)) ? userRegionOptions : [],
            userAdditionalGroupOptions: (_.size(userAdditionalGroupOptions)) ? userAdditionalGroupOptions : [],
            tempPassword: (_.size(tempPasswords)) ? _.first(tempPasswords) : '',
            users: (_.size(users)) ? users : [],
        };

        return _.merge({}, result, this.state.formChanges);
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;

        /*
            // Props values
            const pa = Formatters.getStr(this.props, 'username');

            // Cached values
            const ca = Formatters.getStr(this.originalFormDataCache, 'username');

            // Determine if formdata state and cache needs reset due to changing associd or invid
            if (pa && ca) {
                resetFormData = (pa !== ca) ? true : false;
            }
        */

        return resetFormData;
    }

    public getDefaultNewUserObject(userRoleOptions: any) {
        return _.cloneDeep({
            username: '',
            password: '',
            passwordConfirm: '',
            email: '',
            firstName: '',
            lastName: '',
            title: '',
            inactive: false,
            locked: false,
            passwordChangeRequired: true,
            userRolesAssigned: _.filter(userRoleOptions, (o) => _.includes(DefaultNewUserRoleIds, _.get(o, 'value', []))),
            userRegionsAssigned: [],
            userAdditionalGroupsAssigned: [],
        });
    }

    // RENDERERS
    public renderHeaderButtonToolbar = (formData: any) => {
        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')} ${Formatters.plural(this.appTextCommon('buttons.change'), this.getFormChangesCount(), false)}`,
            };
        }

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleShowModalNewUser(e, formData); }}>
                                        <i className="glyphicon glyphicon-plus" /> {this.appText('new-user')}
                                    </Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderNewUserModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let inputPropsLargeTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 6,
            className: 'inventory-detail-input-full-width',
        });

        let inputPropsSmallTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 4,
            className: 'inventory-detail-input-full-width',
        });

        const inputPropsCheckboxes = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsMultiSelectRoles = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormNewUserRoleChange(n, v); },
        });

        const inputPropsMultiSelectRegions = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormNewUserRegionChange(n, v); },
        });

        const inputPropsMultiSelectAdditionalGroups = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormNewUserAdditionalGroupChange(n, v); },
        });

        const ValidationErrorMessage = (props) => {
            return (
                <Row>
                    <Col xs={defaultInputProps.colLabelXs} />
                    <Col xs={defaultInputProps.colInputXs}>
                        <div className="new-user-username-in-use">{props.children}</div>
                    </Col>
                </Row>
            );
        };

        // Ensure username and email are unique
        const newUserUsername = Formatters.getStr(formData, 'newUser.username');
        const duplicateUsername = UserComponents.IsDuplicateUsername(newUserUsername, formData.users);
        const errorDuplicateUsername = (duplicateUsername) ? (
            <ValidationErrorMessage>
                <span>{this.appText('modals.new-user.username-in-use')}</span>
            </ValidationErrorMessage>
        ) : (<div />);

        const newUserEmail = Formatters.getStr(formData, 'newUser.email');
        const duplicateEmail = UserComponents.IsDuplicateEmail(newUserEmail, formData.users);
        const errorDuplicateEmail = (duplicateEmail) ? (
            <ValidationErrorMessage>
                <span>{this.appText('modals.new-user.email-in-use')}</span>
            </ValidationErrorMessage>
        ) : (<div />);

        const newUserPassword = Formatters.getStr(formData, 'newUser.password');
        const passwordInvalid = newUserPassword && !UserComponents.IsPasswordValid(newUserPassword);
        const errorPasswordInvalid = (passwordInvalid) ? (
            <ValidationErrorMessage>
                <span>{this.appText('modals.new-user.password-min-length', true, String(UserComponents.MinimumPasswordLength))}</span>
            </ValidationErrorMessage>
        ) : (<div />);

        const newUserPasswordConfirm = Formatters.getStr(formData, 'newUser.passwordConfirm');
        const passwordsMismatched = (newUserPassword || newUserPasswordConfirm) && !UserComponents.DoPasswordsMatch(newUserPassword, newUserPasswordConfirm);
        let errorPasswordsMismatched = (passwordsMismatched) ? (
            <ValidationErrorMessage>
                <span>{this.appText('modals.new-user.passwords-mismatched')}</span>
            </ValidationErrorMessage>
        ) : (<div />);

        const temporaryPassword = Formatters.getStr(formData, 'tempPassword');
        const showTemporaryPassword = !(newUserPassword || newUserPasswordConfirm) ? true : false;
        const passwordFieldPlaceholder = (showTemporaryPassword) ? temporaryPassword : this.appText('modals.new-user.required');

        const newUserAssignedRoles = _.get(formData, 'newUser.userRolesAssigned', []);
        const assignedRolesInvalid = !UserComponents.AreAssignedRolesValid(newUserAssignedRoles);
        let errorAssignedRolesInvalid = (assignedRolesInvalid) ? (
            <ValidationErrorMessage>
                <span>{this.appText('modals.new-user.assigned-roles-invalid')}</span>
            </ValidationErrorMessage>
        ) : (<div />);

        const newUserAssignedRegions = _.get(formData, 'newUser.userRegionsAssigned', []);
        const assignedRegionsInvalid = !UserComponents.AreAssignedRegionsValid(newUserAssignedRegions);
        let errorAssignedRegionsInvalid = (assignedRegionsInvalid) ? (
            <ValidationErrorMessage>
                <span>{this.appText('modals.new-user.assigned-regions-invalid')}</span>
            </ValidationErrorMessage>
        ) : (<div />);

        const newUserFormValid = (
            (newUserUsername && !duplicateUsername) &&
            (newUserEmail && !duplicateEmail) &&
            (Formatters.getStr(formData, 'newUser.firstName')) &&
            (Formatters.getStr(formData, 'newUser.lastName')) &&
            (!passwordInvalid && !passwordsMismatched) &&
            (!assignedRolesInvalid) && (!assignedRegionsInvalid)
        ) ? true : false;

        return (
            <Modal
                backdrop="static"
                enforceFocus={false}
                show={this.state.showModalNewUser}
                onHide={() => this.handleCloseModalNewUser()}
                dialogClassName="edit-user-new-user-modal-container"
            >
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.new-user.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="edit-user-new-user-modal">
                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('login-details')}>
                            <KendoWrappers.KendoTextInputDebounced
                                name="newUser.username"
                                label={this.appText('modals.new-user.new-username')}
                                placeholder={this.appText('modals.new-user.required')}
                                value={Formatters.getStr(formData, 'newUser.username')}
                                required={true}
                                {...inputPropsSmallTextDebounced}
                            />

                            {errorDuplicateUsername}

                            <KendoWrappers.KendoTextInputDebounced
                                name="newUser.email"
                                label={this.appText('modals.new-user.email')}
                                value={Formatters.getStr(formData, 'newUser.email')}
                                placeholder={this.appText('modals.new-user.required')}
                                type="email"
                                required={true}
                                {...inputPropsLargeTextDebounced}
                            />

                            {errorDuplicateEmail}

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_tempPassword"
                                label={this.appText('modals.new-user.temporary-password')}
                                rowClassName={(showTemporaryPassword) ? '' : 'hide-temporary-password-fields'}
                                value={<span className="new-user-temporary-password">{temporaryPassword}</span>}
                                {...inputPropsLargeTextDebounced}
                            />

                            <KendoWrappers.KendoTextInputDebounced
                                name="newUser.password"
                                label={this.appText('modals.new-user.password')}
                                placeholder={passwordFieldPlaceholder}
                                value={Formatters.getStr(formData, 'newUser.password')}
                                type="password"
                                required={!showTemporaryPassword}
                                {...inputPropsLargeTextDebounced}
                            />

                            {errorPasswordInvalid}

                            <KendoWrappers.KendoTextInputDebounced
                                name="newUser.passwordConfirm"
                                label={this.appText('modals.new-user.confirm-password')}
                                placeholder={passwordFieldPlaceholder}
                                value={Formatters.getStr(formData, 'newUser.passwordConfirm')}
                                type="password"
                                required={!showTemporaryPassword}
                                {...inputPropsLargeTextDebounced}
                            />

                            <KendoWrappers.KendoCheckbox
                                name="newUser.passwordChangeRequired"
                                label={this.appTextCommon('forms.user.password.must-change-password')}
                                // value={Formatters.getBool(formData, 'newUser.passwordChangeRequired')}
                                value={formData.newUser.passwordChangeRequired}
                                {...inputPropsCheckboxes}
                            />

                            {errorPasswordsMismatched}

                        </DetailFormCommon.InventoryDetailFormSection>

                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('contact-details')}>
                            <KendoWrappers.KendoTextInputDebounced
                                name="newUser.firstName"
                                label={this.appText('modals.new-user.first-name')}
                                placeholder={this.appText('modals.new-user.required')}
                                value={Formatters.getStr(formData, 'newUser.firstName')}
                                required={true}
                                {...inputPropsSmallTextDebounced}
                            />

                            <KendoWrappers.KendoTextInputDebounced
                                name="newUser.lastName"
                                label={this.appText('modals.new-user.last-name')}
                                placeholder={this.appText('modals.new-user.required')}
                                value={Formatters.getStr(formData, 'newUser.lastName')}
                                required={true}
                                {...inputPropsSmallTextDebounced}
                            />

                            <KendoWrappers.KendoTextInputDebounced
                                name="newUser.title"
                                label={this.appText('modals.new-user.professional-title')}
                                value={Formatters.getStr(formData, 'newUser.title')}
                                {...inputPropsLargeTextDebounced}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>

                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-details')}>
                            <KendoWrappers.KendoCheckbox
                                name="newUser.inactive"
                                label={this.appText('security-inactive')}
                                value={formData.newUser.inactive}
                                {...inputPropsCheckboxes}
                            />

                            <KendoWrappers.KendoCheckbox
                                name="newUser.locked"
                                label={this.appText('security-locked')}
                                value={formData.newUser.locked}
                                {...inputPropsCheckboxes}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>

                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-roles')}>
                            <KendoWrappers.KendoMultiSelect
                                name="newUser.userRolesAssigned"
                                label={this.appText('security-roles')}
                                textField="text"
                                dataItemKey="value"
                                required={true}
                                data={_.get(formData, 'userRoleOptions', [])}
                                value={_.get(formData, 'newUser.userRolesAssigned', [])}
                                {...inputPropsMultiSelectRoles}
                            />

                            {errorAssignedRolesInvalid}

                        </DetailFormCommon.InventoryDetailFormSection>

                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-regions')}>
                            <KendoWrappers.KendoMultiSelect
                                name="newUser.userRegionsAssigned"
                                label={this.appText('security-regions')}
                                textField="text"
                                dataItemKey="value"
                                required={true}
                                data={_.get(formData, 'userRegionOptions', [])}
                                value={_.get(formData, 'newUser.userRegionsAssigned', [])}
                                {...inputPropsMultiSelectRegions}
                            />

                            {errorAssignedRegionsInvalid}

                        </DetailFormCommon.InventoryDetailFormSection>

                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-assigned-additional-groups')}>
                            <KendoWrappers.KendoMultiSelect
                                name="newUser.userAdditionalGroupsAssigned"
                                label={this.appText('security-additional-groups')}
                                textField="text"
                                dataItemKey="value"
                                required={false}
                                data={_.get(formData, 'userAdditionalGroupOptions', [])}
                                value={_.get(formData, 'newUser.userAdditionalGroupsAssigned', [])}
                                {...inputPropsMultiSelectAdditionalGroups}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="success"
                        disabled={!newUserFormValid}
                        onClick={(e) => this.handleNewUserButton(e)}
                    >
                        {this.appText('new-user')}
                    </Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalNewUser()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { lookups, ...rest } = this.props;

        let pageTitle = this.appTextPageTitle();
        let page = (<div />);

        if (!_.isEmpty(lookups)) {
            const formData = this.getFormData(lookups);

            /* console.log('pageData', {
                formData: formData,
                numChanges: this.getFormChangesCount(),
                formChanges: this.state.formChanges,
                state_showModalNewUser: this.state.showModalNewUser,
            }); */

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            page = (
                <div>
                    {this.renderNewUserModal(formData)}

                    <Row>
                        <Col xs={12} xl={9}>
                            {/* Header toolbar */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar(formData)}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.Users,
    LK.LookupKeys.Roles,
    LK.LookupKeys.Regions,
    LK.LookupKeys.TempPasswords,
    LK.LookupKeys.Groups,
]);

const mapPropsToConfigs = (props) => {
    return ReduxQueries.Queries.getLookupsByKeyRequest(
        getApiUrlLookups(),
        ReduxQueries.PropsAccessToken(props)
    );
};

const mapStateToProps = (state, props) => {
    const mapped = ReduxQueries.MapQueryStateToPropsByUrl(getApiUrlLookups(), state, props, {
        lastRequestDate: new Date().getTime(),
    });

    return mapped;
};

const mapDispatchToProps = {
    newUser: MutateActions.newUserMutateAsync,
    navigateTo: (path) => push(path),
};

const AddUserContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AddUser);

export default withRouter(AddUserContainer);



// WEBPACK FOOTER //
// ./src/user/add.tsx